import { useState, type ReactNode } from 'react';
import { cx } from './cx.js';
import { useInputMode } from './useInputMode.js';

/**
 * Barre d'actions d'un formulaire. Enfants dans l'ordre : action secondaire (« Retour », « Annuler »,
 * facultative) puis action principale (bouton `type="submit"` en général).
 *
 * - Téléphone, hors saisie : dans le flux, en bas du formulaire ; les deux boutons **côte à côte**
 *   (« Retour » plus étroit) pour gagner de la hauteur.
 * - Téléphone, clavier ouvert (`useInputMode`) et champ actif **dans ce formulaire** : une seule ligne
 *   compacte (≤ 56 px) posée juste au-dessus du clavier : « Retour » en lien discret à gauche, bouton
 *   principal à droite. La navigation basse est masquée par la coque pendant ce temps. Un autre
 *   formulaire de la page (champ de recherche, autre carte) ne fait pas flotter cette barre.
 * - Ordinateur : boutons alignés à droite, dans le flux.
 *
 * `inset` : formulaire posé dans une carte (édition en place) : pas de filet ni de grande marge.
 * `sticky` : formulaire long ou par étapes : la barre reste collée en bas de l'écran, au-dessus de la
 * navigation basse (téléphone) ou du bas de la fenêtre (ordinateur), l'action principale toujours à portée.
 */
export function FormActions({
  children,
  className,
  inset = false,
  sticky = false,
}: {
  children: ReactNode;
  className?: string;
  inset?: boolean;
  sticky?: boolean;
}) {
  const { typing, viewportTop, viewportHeight, active } = useInputMode();
  // Ancre dans le flux (élément gardé en état : il désigne le formulaire auquel la barre appartient).
  const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);
  const row = cx(
    'flex items-center gap-2',
    // Un seul bouton : pleine largeur sur téléphone. Deux : « Retour » étroit, principal extensible.
    '[&>*]:min-w-0 [&>*:last-child]:flex-1 sm:justify-end sm:[&>*:last-child]:flex-none',
    '[&>*:first-child:not(:last-child)]:shrink-0 [&>*:first-child:not(:last-child)]:basis-[32%] sm:[&>*:first-child:not(:last-child)]:basis-auto',
  );
  const scope = anchor ? (anchor.closest('form') ?? anchor.parentElement) : null;
  // Au premier rendu (ancre pas encore posée), on reste dans le flux.
  const floating = typing && !!scope && !!active && scope.contains(active);
  if (floating) {
    return (
      <>
        {/* Place réservée dans le flux : rien ne saute quand la barre devient flottante. */}
        <div ref={setAnchor} aria-hidden className="h-14" />
        <div
          data-form-actions="typing"
          className={cx(
            'fixed inset-x-0 z-40 -translate-y-full border-t border-line bg-surface/97 px-[var(--page-x)] py-[5px] shadow-md backdrop-blur-md',
            className,
          )}
          style={{ top: viewportTop + viewportHeight }}
          // Le champ garde le focus pendant l'appui : sinon le focus passe au bouton, le mode saisie
          // s'arrête, la barre retourne dans le flux et le clic (Android, Chrome) se perd.
          onMouseDown={(e) => e.preventDefault()}
        >
          <div
            className={cx(
              'mx-auto flex h-11 max-w-3xl items-center justify-between gap-3',
              // « Retour » en lien discret ; bouton principal compact à droite.
              '[&>*:first-child:not(:last-child)]:min-h-11 [&>*:first-child:not(:last-child)]:bg-transparent [&>*:first-child:not(:last-child)]:px-1 [&>*:first-child:not(:last-child)]:text-sm [&>*:first-child:not(:last-child)]:text-ink-2 [&>*:first-child:not(:last-child)]:underline [&>*:first-child:not(:last-child)]:underline-offset-4 [&>*:first-child:not(:last-child)]:shadow-none [&>*:first-child:not(:last-child)]:ring-0',
              '[&>*:last-child]:ml-auto [&>*:last-child]:min-h-11 [&>*:last-child]:px-4 [&>*:last-child]:text-base',
            )}
          >
            {children}
          </div>
        </div>
      </>
    );
  }
  return (
    <div
      ref={setAnchor}
      data-form-actions="flow"
      className={cx(
        sticky
          ? 'sticky bottom-[calc(var(--bottom-nav-h)+var(--safe-bottom))] z-10 -mx-[var(--page-x)] mt-4 border-t border-line bg-canvas/95 px-[var(--page-x)] py-2 backdrop-blur-md lg:bottom-0 lg:mx-0 lg:px-0'
          : inset
            ? 'mt-1'
            : 'mt-4 border-t border-line pt-3 lg:border-0 lg:pt-0',
        className,
      )}
    >
      <div className={row}>{children}</div>
    </div>
  );
}
